import { useMutation, useQuery, useQueryClient } from 'react-query';
import notificationError from '@/utils/functions/notificationError';
import {
  ChecklistObservationsListRequest,
  ChecklistSignatureWithTokenRequest,
  ReportChecklistRequest,
  WorkOrderChecklistAccessoryListRequest,
  WorkOrderChecklistComplementItemsListRequest,
  WorkOrderChecklistDamageListRequest,
  WorkOrderChecklistFutureServiceListRequest,
  WorkOrderChecklistQuickListRequest,
  WorkOrderImagesListRequest,
} from './WorkOrdersChecklist.types';
import workorderChecklistServices from './WorkOrdersChecklist.service';
import { notification } from 'antd';
import { WORKORDER_ITEMS_QUERY_KEY } from '../WorkOrdersItems/WorkOrdersItems.hooks';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useWorkOrderChecklistImageList(
  params?: WorkOrderImagesListRequest,
  enabled = true,
) {
  return useQuery(
    ['workOrders/checklist/images', params],
    () => workorderChecklistServices.listImages(params),
    {
      enabled:
        enabled && (!!params?.diagnosticId || !!params?.workOrderComplementId),
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderChecklistImageCreate() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.addImages, {
    async onSuccess() {
      notification.success({ message: 'Imagens cadastradas com sucesso' });
      await queryClient.invalidateQueries(['workOrders/checklist/images']);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistImageRemove() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.removeImages, {
    async onSuccess() {
      notification.success({ message: 'Imagem removida com sucesso' });
      await queryClient.invalidateQueries(['workOrders/checklist/images']);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistImageRemoveMultiple() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.removeMultipleImages, {
    async onSuccess() {
      notification.success({ message: 'Imagens removidas com sucesso' });
      await queryClient.invalidateQueries(['workOrders/checklist/images']);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistRequestServiceList(
  params?: WorkOrderImagesListRequest,
) {
  return useQuery(
    ['workOrders/checklist/requestService', params],
    () => workorderChecklistServices.listRequestService(params),
    {
      enabled: !!params?.diagnosticId || !!params?.workOrderComplementId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderChecklistRequestServiceCreate() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.addRequestService, {
    async onSuccess() {
      notification.success({
        message: 'Serviço solicitado cadastrado com sucesso',
      });
      await queryClient.invalidateQueries([
        'workOrders/checklist/requestService',
      ]);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistRequestServiceRemove() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.removeRequestService, {
    async onSuccess() {
      notification.success({
        message: 'Serviço solicitado removido com sucesso',
      });
      await queryClient.invalidateQueries([
        'workOrders/checklist/requestService',
      ]);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistRequestServiceUpdate() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.updateRequestService, {
    async onSuccess() {
      notification.success({
        message: 'Serviço solicitado atualizado com sucesso',
      });
      await queryClient.invalidateQueries([
        'workOrders/checklist/requestService',
      ]);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistQuickList(
  params?: WorkOrderChecklistQuickListRequest,
) {
  return useQuery(
    ['workOrders/checklist/quick', params],
    () => workorderChecklistServices.listQuick(params),
    {
      enabled: !!params?.diagnosticId || !!params?.workOrderComplementId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderChecklistQuickCreate() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.addQuick, {
    async onSuccess() {
      notification.success({
        message: 'Checklist rápido atualizado com sucesso',
      });
      await queryClient.invalidateQueries(['workOrders/checklist/quick']);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistAccessoryList(
  params?: WorkOrderChecklistAccessoryListRequest,
) {
  return useQuery(
    ['workOrders/checklist/accessory', params],
    () => workorderChecklistServices.listAccessory(params),
    {
      enabled: !!params?.diagnosticId || !!params?.workOrderComplementId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderChecklistAccessoryCreate() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.addAccessory, {
    async onSuccess() {
      notification.success({
        message: 'Acessórios atualizado com sucesso',
      });
      await queryClient.invalidateQueries(['workOrders/checklist/accessory']);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistComplementItemList(
  params?: WorkOrderChecklistComplementItemsListRequest,
) {
  return useQuery(
    ['workOrders/checklist/complementItem', params],
    () => workorderChecklistServices.listComplementItem(params),
    {
      enabled: !!params?.diagnosticId || !!params?.workOrderComplementId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderChecklistComplementItemCreate() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.addComplementItem, {
    async onSuccess() {
      notification.success({
        message: 'Itens do complemento atualizado com sucesso',
      });
      await queryClient.invalidateQueries([
        'workOrders/checklist/complementItem',
      ]);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistDamageList(
  params?: WorkOrderChecklistDamageListRequest,
) {
  return useQuery(
    ['workOrders/checklist/damage', params],
    () => workorderChecklistServices.listDamage(params),
    {
      enabled: !!params?.diagnosticId || !!params?.workOrderComplementId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderChecklistDamageCreate() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.addDamage, {
    async onSuccess() {
      notification.success({
        message: 'Avarias do veículo atualizado com sucesso',
      });
      await queryClient.invalidateQueries(['workOrders/checklist/damage']);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistFutureServiceList(
  params?: WorkOrderChecklistFutureServiceListRequest,
) {
  return useQuery(
    ['workOrders/checklist/futureService', params],
    () => workorderChecklistServices.listFutureService(params),
    {
      enabled: !!params?.diagnosticId || !!params?.workOrderComplementId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderChecklistFutureServiceToWorkorder() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.sendFutureServiceToWorkorder, {
    async onSuccess() {
      notification.success({
        message:
          'Envio de serviços futuro para o orçamento realizado com sucesso',
      });
      await queryClient.invalidateQueries([
        'workOrders/checklist/futureService',
      ]);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistFutureServiceImportExcludedServices() {
  const queryClient = useQueryClient();

  return useMutation(
    workorderChecklistServices.importFutureServiceExcludedServices,
    {
      async onSuccess() {
        notification.success({
          message: 'Importação de serviços futuro realizado com sucesso',
        });
        await queryClient.invalidateQueries([
          'workOrders/checklist/futureService',
        ]);
        await queryClient.invalidateQueries([
          `${WORKORDER_ITEMS_QUERY_KEY}/verify`,
        ]);
        await queryClient.invalidateQueries(['workorder/checklistReport']);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useWorkOrderChecklistFutureServiceCreate() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.addFutureService, {
    async onSuccess() {
      notification.success({
        message: 'Serviço futuro cadastrado com sucesso',
      });
      await queryClient.invalidateQueries([
        'workOrders/checklist/futureService',
      ]);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistFutureServiceEdit() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.editFutureService, {
    async onSuccess() {
      notification.success({
        message: 'Serviço futuro editado com sucesso',
      });
      await queryClient.invalidateQueries([
        'workOrders/checklist/futureService',
      ]);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderChecklistFutureServiceRemove() {
  const queryClient = useQueryClient();

  return useMutation(workorderChecklistServices.removeFutureService, {
    async onSuccess() {
      notification.success({
        message: 'Serviço futuro removido com sucesso',
      });
      await queryClient.invalidateQueries([
        'workOrders/checklist/futureService',
      ]);
      await queryClient.invalidateQueries(['workorder/checklistReport']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useReportChecklist() {
  return useMutation(workorderChecklistServices.getChecklistReport, {
    onError(error) {
      notificationError(error);
    },
  });
}

export function useReportChecklistQuery(params: ReportChecklistRequest) {
  return useQuery(
    ['workorder/checklistReport', { ...params }],
    () => workorderChecklistServices.getChecklistReport(params),
    {
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useReportChecklistPublicQuery(token?: string) {
  return useQuery(
    ['workorder/checklistReportPublic', { token }],
    () => workorderChecklistServices.getChecklistReportPublic(token),
    {
      enabled: !!token,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useChecklistSignatureWithToken() {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    (params: ChecklistSignatureWithTokenRequest) =>
      workorderChecklistServices.getChecklistSignatureWithToken(params),
    {
      async onSuccess() {
        await queryClient.invalidateQueries([
          'workorder/checklistReportPublic',
        ]);
        await queryClient.invalidateQueries(['checklist-signature']);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
  return mutation;
}

export function useChecklistObservation(
  params?: ChecklistObservationsListRequest,
) {
  return useQuery(
    ['workorder/checklist/observations', { ...params }],
    () => workorderChecklistServices.getChecklistObservations(params),
    { enabled: !!params?.diagnosticId || !!params?.workOrderComplementId },
  );
}

export function useCheckListObservationCreate() {
  const queryChanel = useQueryChanel();

  return useMutation(workorderChecklistServices.addChecklistObservation, {
    onSuccess() {
      notification.success({
        message: 'Observação adicionada',
      });

      queryChanel({
        invalidateQueries: [['workorder/checklist/observations']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCheckListObservationUpdate() {
  const queryChanel = useQueryChanel();

  return useMutation(workorderChecklistServices.editChecklistObservation, {
    onSuccess() {
      notification.success({
        message: 'Observação atualizada',
      });

      queryChanel({
        invalidateQueries: [['workorder/checklist/observations']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCheckListObservationRemove() {
  const queryChanel = useQueryChanel();
  return useMutation(workorderChecklistServices.removeChecklistObservation, {
    onSuccess() {
      notification.success({
        message: 'Observação removida',
      });

      queryChanel({
        invalidateQueries: [['workorder/checklist/observations']],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
